import React, { useRef, useState } from 'react';
import { Alert, Button, Checkbox, Input, Space, Tag, Typography, Upload, message } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { userMessageForError } from '../../utils/apiError';
import {
    ATTESTATION_STATEMENT_FALLBACK,
    describeExtractError,
    LIMITS,
    buildCuratePayload,
    describeCurationError,
    summariseUsage,
    validateCurateInput,
} from '../../utils/questionBankCuratorModel';

const { Text } = Typography;

/**
 * "Draft with AI" (Sheet 2 row 3). The admin picks a clause and supplies sources - pasted text, an
 * uploaded .txt/.md/.docx, or a URL on the approved host list. The model only ever sees those
 * sources; every candidate it returns must quote them verbatim (checked server-side). The result is
 * AI DRAFTS ONLY: nothing is live until a person approves each draft and the version is published
 * (Commandment IV). The admin's remaining daily caps are shown before they spend one.
 */
const AiCurateForm = ({ standardId, standardLabel, onRunComplete }) => {
    const { request } = useApiRequest();
    const nextId = useRef(1);
    const [targetClause, setTargetClause] = useState('');
    const [adminNote, setAdminNote] = useState('');
    const [sources, setSources] = useState([]);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [formError, setFormError] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [retryFile, setRetryFile] = useState(null); // kept when the extractor was busy, so the choice is not lost
    const [errorView, setErrorView] = useState(null);
    const [attested, setAttested] = useState(false);
    const [attestationStatement, setAttestationStatement] = useState(ATTESTATION_STATEMENT_FALLBACK);

    const usage = useResource(async () => (await request('GET', '/admin/question-bank/ai-curate/usage')).data, [request]);
    const domains = useResource(
        async () => (await request('GET', '/admin/question-bank/source-domains')).data.domains || [],
        [request],
    );
    const caps = summariseUsage(usage.data);
    const approvedHosts = (domains.data || []).filter((d) => d.active).map((d) => d.hostname);

    const addSource = (s) => setSources((cur) => [...cur, { id: nextId.current++, ...s }]);
    const updateSource = (id, patch) => setSources((cur) => cur.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    const removeSource = (id) => setSources((cur) => cur.filter((s) => s.id !== id));

    const handleFile = async (file) => {
        setUploading(true);
        setUploadError(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const { data } = await request('POST', '/admin/question-bank/ai-curate/extract-text', { formData });
            addSource({ kind: 'text', text: data.text, label: data.label, via: 'upload', truncated: !!data.truncated, chars: data.chars });
            setRetryFile(null);
        } catch (err) {
            const view = describeExtractError(err);
            setUploadError(view.message);
            setRetryFile(view.retry ? file : null);
        } finally {
            setUploading(false);
        }
    };

    const submit = async () => {
        const input = { standardId, targetClause, sources, adminNote, attested };
        const invalid = validateCurateInput(input);
        if (invalid) {
            setFormError(invalid);
            return;
        }
        setBusy(true);
        setFormError(null);
        setErrorView(null);
        try {
            const { data } = await request('POST', '/admin/question-bank/ai-curate', { body: buildCuratePayload(input) });
            message.success(`${data.drafts?.length || 0} AI draft(s) created. None are live until you approve and publish them.`);
            await onRunComplete(data);
        } catch (err) {
            const view = describeCurationError(err);
            setErrorView(view);
            if (view.kind === 'attestation') {
                // The server's own wording wins; the admin must tick that statement, not our fallback.
                if (view.statement) setAttestationStatement(view.statement);
                setAttested(false);
            }
            if (view.refreshUsage) usage.reload();
        } finally {
            setBusy(false);
            usage.reload();
        }
    };

    const blockedByCaps = !!caps?.exhausted;
    const urlCount = sources.filter((s) => s.kind === 'url').length;

    return (
        <div>
            <Alert
                type="warning"
                style={{ marginBottom: 12 }}
                message="The AI only drafts. A person approves every question."
                description={`Drafts are created for ${standardLabel || 'the selected standard'} and stay out of every project until you approve each one and publish the version.`}
            />

            <div data-testid="ai-usage" style={{ marginBottom: 12 }}>
                {usage.status === 'error' && <Alert type="warning" message={`Daily caps could not be loaded. ${userMessageForError(usage.error)}`} />}
                {caps && (
                    <Space direction="vertical" size={0}>
                        <Text>{caps.runsText}</Text>
                        <Text>{caps.tokensText}</Text>
                        <Text type="secondary">{caps.perMinuteText}</Text>
                    </Space>
                )}
                {blockedByCaps && <Alert type="error" style={{ marginTop: 8 }} message="You have used your AI allowance for the last 24 hours. Try again later." data-testid="caps-exhausted" />}
            </div>

            <Text strong>Target clause</Text>
            <Input
                aria-label="Target clause"
                placeholder="e.g. A.5.15, Section 8, Article 32"
                style={{ marginBottom: 12 }}
                value={targetClause}
                onChange={(e) => setTargetClause(e.target.value)}
            />

            <Text strong>Sources</Text>
            <div style={{ margin: '4px 0 8px' }}>
                <Text type="secondary">
                    Up to {LIMITS.maxSources} sources per run (at most {LIMITS.maxUrlSources} URLs). Each pasted text needs at least {LIMITS.minTextChars} characters.
                    PDFs are not read: paste their text instead.
                </Text>
            </div>
            {sources.map((s, idx) => (
                <div key={s.id} data-testid="ai-source" style={{ border: '1px solid var(--color-border)', borderRadius: 6, padding: 8, marginBottom: 8 }}>
                    <Space style={{ marginBottom: 4 }}>
                        <Tag>Source {idx + 1}</Tag>
                        <Text type="secondary">{s.kind === 'url' ? 'URL' : s.via === 'upload' ? 'Uploaded document' : 'Pasted text'}</Text>
                        <Button size="small" onClick={() => removeSource(s.id)}>Remove</Button>
                    </Space>
                    {s.kind === 'url' ? (
                        <Input aria-label={`Source ${idx + 1} URL`} placeholder="https://..." value={s.url} onChange={(e) => updateSource(s.id, { url: e.target.value })} />
                    ) : (
                        <div>
                            <Input aria-label={`Source ${idx + 1} label`} placeholder="Label (optional), e.g. DPDP Rules 2025, Rule 6" style={{ marginBottom: 4 }} value={s.label || ''} onChange={(e) => updateSource(s.id, { label: e.target.value })} />
                            <Input.TextArea aria-label={`Source ${idx + 1} text`} rows={5} value={s.text} onChange={(e) => updateSource(s.id, { text: e.target.value })} />
                            {s.truncated && (
                                <Text type="warning" data-testid="upload-truncated">
                                    This document was longer than {LIMITS.maxTextChars.toLocaleString('en-IN')} characters; only the first part was kept.
                                </Text>
                            )}
                        </div>
                    )}
                </div>
            ))}
            <Space wrap style={{ marginBottom: 8 }}>
                <Button onClick={() => addSource({ kind: 'text', text: '', label: '', via: 'paste' })} disabled={sources.length >= LIMITS.maxSources}>Paste text</Button>
                <Upload
                    accept=".txt,.md,.docx"
                    showUploadList={false}
                    disabled={sources.length >= LIMITS.maxSources}
                    beforeUpload={(file) => { handleFile(file); return false; }}
                >
                    <Button loading={uploading} disabled={sources.length >= LIMITS.maxSources}>Upload document</Button>
                </Upload>
                <Button onClick={() => addSource({ kind: 'url', url: '' })} disabled={sources.length >= LIMITS.maxSources || urlCount >= LIMITS.maxUrlSources}>Add URL</Button>
            </Space>
            <div style={{ marginBottom: 8 }}>
                <Text type="secondary" data-testid="approved-hosts">
                    {approvedHosts.length > 0
                        ? `Approved URL hosts: ${approvedHosts.join(', ')}.`
                        : domains.status === 'error'
                            ? 'The approved host list could not be loaded.'
                            : 'No approved URL hosts yet. Add one under Source domains to use URLs.'}
                </Text>
            </div>
            {uploadError && (
                <Alert
                    type="error"
                    message={uploadError}
                    data-testid="upload-error"
                    style={{ marginBottom: 8 }}
                    action={retryFile ? <Button size="small" loading={uploading} onClick={() => handleFile(retryFile)}>{`Try ${retryFile.name || 'the file'} again`}</Button> : null}
                />
            )}

            <Text strong>Note for the model (optional)</Text>
            <Input.TextArea
                aria-label="Note for the model"
                rows={2}
                style={{ marginBottom: 12 }}
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
            />

            <div style={{ marginBottom: 12 }}>
                <Checkbox checked={attested} onChange={(e) => setAttested(e.target.checked)}>
                    <span data-testid="attestation-statement">{attestationStatement}</span>
                </Checkbox>
                <div><Text type="secondary" style={{ fontSize: 12 }}>The text you supply is sent to a third-party AI service. Required before drafting.</Text></div>
            </div>

            {formError && <Alert type="error" message={formError} data-testid="curate-form-error" style={{ marginBottom: 12 }} />}
            {errorView && (
                <Alert
                    type="error"
                    style={{ marginBottom: 12 }}
                    data-testid={`curate-error-${errorView.kind}`}
                    message={errorView.message}
                    description={(
                        <div>
                            {errorView.refusals.length > 0 && (
                                <div>
                                    <div>Nothing was sent to the model. These sources were refused:</div>
                                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                                        {errorView.refusals.map((r, i) => <li key={i}>{r}</li>)}
                                    </ul>
                                </div>
                            )}
                            {errorView.note && <div>{errorView.note}</div>}
                        </div>
                    )}
                />
            )}

            <Button data-testid="ai-submit" onClick={submit} loading={busy} disabled={busy || blockedByCaps || !attested}>Draft with AI</Button>
            {busy && <Text type="secondary" style={{ marginLeft: 8 }}>Drafting; this can take a minute or two.</Text>}
        </div>
    );
};

export default AiCurateForm;
